import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Edit3,
  Copy,
  Trash2,
  CheckCircle2,
  Circle,
  Globe,
  MoreHorizontal,
  LayoutTemplate,
  History,
  Link2,
} from 'lucide-react';

import { useCMSContentTypesQuery, useCMSListQuery, useCMSTemplatesQuery } from '@/client/api/cms/cms.query';
import {
  useApplyTemplateMutation,
  useDeleteCMSMutation,
  useDuplicateCMSMutation,
  usePublishCMSMutation,
  useUnpublishCMSMutation,
} from '@/client/api/cms/cms.mutation';
import {
  CMSDisplayStatus,
  CMSObject,
} from '@/client/types/cms';
import CMSPageHistoryPanel from '@/client/components/cms/CMSPageHistoryPanel';
import CMSMenuLinksPanel from '@/client/components/cms/CMSMenuLinksPanel';

import Pagination from '@/client/components/common/Pagination';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const STATUSES: { value: CMSDisplayStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Drafts' },
  { value: 'published', label: 'Published' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
  { value: 'deleted', label: 'Deleted' },
];

const LOCALES = ['en_GB', 'en_US', 'fr_FR', 'es_ES', 'pt_BR', 'de_DE'];
const COUNTRIES = ['US', 'GB', 'NG', 'KE', 'ZA', 'FR', 'DE'];

const StatusPill = ({ status }: { status?: CMSDisplayStatus }) => {
  const s = status ?? 'draft';
  const styles: Record<CMSDisplayStatus, string> = {
    draft: 'bg-gray-100 text-gray-700 border-gray-200',
    published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-amber-50 text-amber-700 border-amber-200',
    expired: 'bg-orange-50 text-orange-700 border-orange-200',
    deleted: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  const Icon = s === 'published' ? CheckCircle2 : Circle;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${styles[s]}`}>
      <Icon className="h-3 w-3" />
      {s}
    </span>
  );
};

const CMSPagesListPage = () => {
  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Pages' },
  ]);

  const navigate = useNavigate();
  const [templateOpen, setTemplateOpen] = useState(false);
  const [historyPage, setHistoryPage] = useState<CMSObject | null>(null);
  const [drawerTab, setDrawerTab] = useState<'history' | 'menus'>('history');

  const {
    page,
    limit,
    filters,
    searchTerm,
    debouncedSearchTerm,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleSearch,
  } = useListFilters<{
    status: CMSDisplayStatus | '';
    ctype: string;
    locale: string;
    country_iso2: string;
  }>({
    // useListFilters persists these (and q/page/limit) in the URL, so a reload
    // restores the same filtered page.
    initialFilters: { status: '', ctype: '', locale: '', country_iso2: '' },
  });

  const { data: typesData } = useCMSContentTypesQuery();
  const { data: templatesData } = useCMSTemplatesQuery();
  const { data, isLoading, isError, error, refetch } = useCMSListQuery(page, {
    limit,
    ...(filters.status ? { status: filters.status } : {}),
    ...(filters.ctype ? { ctype: filters.ctype } : {}),
    ...(filters.locale ? { locale: filters.locale } : {}),
    ...(filters.country_iso2 ? { country_iso2: filters.country_iso2 } : {}),
    ...(debouncedSearchTerm ? { q: debouncedSearchTerm } : {}),
  });

  const publishMutation = usePublishCMSMutation();
  const unpublishMutation = useUnpublishCMSMutation();
  const deleteMutation = useDeleteCMSMutation();
  const dupMutation = useDuplicateCMSMutation();
  const applyMutation = useApplyTemplateMutation();

  const pages: CMSObject[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const hasMore = data?.hasMore ?? false;
  const templates = templatesData ?? [];

  const handlePublish = async (p: CMSObject) => {
    try {
      await publishMutation.mutateAsync(p.cmsoid);
      toast.success(`Published "${p.title}"`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to publish');
    }
  };

  const handleUnpublish = async (p: CMSObject) => {
    try {
      await unpublishMutation.mutateAsync(p.cmsoid);
      toast.success(`Unpublished "${p.title}"`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to unpublish');
    }
  };

  const handleDuplicate = async (p: CMSObject) => {
    try {
      const copy = await dupMutation.mutateAsync(p.cmsoid);
      toast.success('Page duplicated');
      if (copy?.cmsoid) navigate(`/admin/content/pages/${copy.cmsoid}`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to duplicate');
    }
  };

  const handleDelete = async (p: CMSObject) => {
    if (!window.confirm(`Delete "${p.title}"? This soft-deletes the page.`)) return;
    try {
      await deleteMutation.mutateAsync({ cmsoid: p.cmsoid });
      toast.success('Page deleted');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to delete');
    }
  };

  const handleApplyTemplate = async (ctplid: number, name: string) => {
    try {
      const page = await applyMutation.mutateAsync({
        ctplid,
        overrides: { title: name },
      });
      setTemplateOpen(false);
      toast.success('Draft created from template');
      if (page?.cmsoid) navigate(`/admin/content/pages/${page.cmsoid}`);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to apply template');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-3">
              <FileText className="h-7 w-7 text-blue-700" />
              <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
            </div>
            <p className="ml-10 text-sm text-gray-500">
              Manage CMS pages served on the public site.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setTemplateOpen(true)} className="gap-2">
              <LayoutTemplate className="h-4 w-4" />
              New from template
            </Button>
            <Button onClick={() => navigate('/admin/content/pages/new')} className="gap-2">
              <Plus className="h-4 w-4" />
              New page
            </Button>
          </div>
        </div>

        <div className="mb-4 rounded-lg border bg-white p-4 shadow">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                className="pl-9"
                placeholder="Search by title, slug, summary…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={filters.ctype}
                onChange={(e) => handleFilterChange('ctype', e.target.value)}
              >
                <option value="">All types</option>
                {(typesData ?? []).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>

              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={filters.locale}
                onChange={(e) => handleFilterChange('locale', e.target.value)}
              >
                <option value="">All locales</option>
                {LOCALES.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>

              <select
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={filters.country_iso2}
                onChange={(e) => handleFilterChange('country_iso2', e.target.value)}
              >
                <option value="">All countries</option>
                {COUNTRIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Title', 'Type', 'Status', 'Locale', 'Updated', ''].map((h) => (
                    <th
                      key={h || 'actions'}
                      className={`px-6 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${h === '' ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {isLoading && Array.from({ length: limit }).map((_, i) => (
                  <tr key={i}>
                    {[...Array(6)].map((__, j) => (
                      <td key={j} className="px-6 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))}

                {!isLoading && isError && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-500">
                      <p className="font-medium text-rose-700">Couldn’t load pages</p>
                      <p className="mt-1 text-gray-500">
                        {(error as Error)?.message || 'Request failed'}
                      </p>
                      <Button variant="outline" className="mt-3" size="sm" onClick={() => refetch()}>
                        Retry
                      </Button>
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && pages.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-500">
                      <FileText className="mx-auto mb-2 h-10 w-10 text-gray-300" />
                      {filters.status || filters.ctype || filters.locale || filters.country_iso2 || debouncedSearchTerm
                        ? 'No pages match these filters.'
                        : 'No CMS pages in staging yet.'}
                      <div className="mt-3">
                        <Button onClick={() => navigate('/admin/content/pages/new')} size="sm" className="gap-2">
                          <Plus className="h-3.5 w-3.5" />
                          Create your first page
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}

                {!isLoading && !isError && pages.map((p) => (
                  <tr key={p.cmsoid} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <button
                        onClick={() => navigate(`/admin/content/pages/${p.cmsoid}`)}
                        className="text-left"
                      >
                        <div className="text-sm font-medium text-gray-900 hover:text-blue-700">
                          {p.title || <span className="italic text-gray-400">(untitled)</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-gray-500 font-mono">
                          {p.slug}
                        </div>
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <span className="rounded border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                        {p.ctype}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-600">
                      <div className="flex items-center gap-1">
                        <Globe className="h-3 w-3 text-gray-400" />
                        {p.locale ?? 'global'}
                        {p.country_iso2 && <span className="text-gray-400">/{p.country_iso2}</span>}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-xs text-gray-500">
                      {p.date_modified
                        ? new Date(p.date_modified).toLocaleString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZoneName: 'short',
                          })
                        : '—'}
                    </td>
                    <td className="whitespace-nowrap px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="rounded p-1.5 hover:bg-gray-100">
                            <MoreHorizontal className="h-4 w-4 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`/admin/content/pages/${p.cmsoid}`)}>
                            <Edit3 className="mr-2 h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setDrawerTab('history');
                              setHistoryPage(p);
                            }}
                          >
                            <History className="mr-2 h-4 w-4" /> History & slugs
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setDrawerTab('menus');
                              setHistoryPage(p);
                            }}
                          >
                            <Link2 className="mr-2 h-4 w-4" /> Menu links
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/admin/content/pages/${p.cmsoid}/preview`)}>
                            <Eye className="mr-2 h-4 w-4" /> Preview
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {p.status !== 'published' ? (
                            <DropdownMenuItem onClick={() => handlePublish(p)}>
                              <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
                              Publish
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleUnpublish(p)}>
                              <Circle className="mr-2 h-4 w-4 text-gray-500" />
                              Unpublish
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleDuplicate(p)}>
                            <Copy className="mr-2 h-4 w-4" /> Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleDelete(p)}
                            className="text-red-600 focus:text-red-700"
                          >
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <Pagination
              total={total}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showSelect
              selectOptions={[10, 20, 30, 50]}
              hasMore={hasMore}
              compact
            />
          </div>
        </div>
      </div>

      <Sheet
        open={!!historyPage}
        onOpenChange={(open) => {
          if (!open) setHistoryPage(null);
        }}
      >
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {drawerTab === 'menus' ? (
                <Link2 className="h-4 w-4" />
              ) : (
                <History className="h-4 w-4" />
              )}
              {drawerTab === 'menus' ? 'Menu links' : 'History'}
            </SheetTitle>
          </SheetHeader>
          {historyPage && (
            <div className="mt-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {historyPage.title || '(untitled)'}
                </p>
                <p className="font-mono text-xs text-gray-500">{historyPage.slug}</p>
              </div>
              <div className="flex gap-1 rounded-md border border-gray-200 bg-gray-50 p-0.5">
                <button
                  type="button"
                  onClick={() => setDrawerTab('history')}
                  className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium ${
                    drawerTab === 'history'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <History className="h-3.5 w-3.5" /> History
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerTab('menus')}
                  className={`flex flex-1 items-center justify-center gap-1 rounded px-2 py-1.5 text-xs font-medium ${
                    drawerTab === 'menus'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Link2 className="h-3.5 w-3.5" /> Menus
                </button>
              </div>
              {drawerTab === 'history' ? (
                <CMSPageHistoryPanel
                  cmsoid={historyPage.cmsoid}
                  currentSlug={historyPage.slug}
                  onRestored={() => {
                    refetch();
                    toast.success('Open the editor to review the restored content');
                  }}
                />
              ) : (
                <CMSMenuLinksPanel
                  cmsoid={historyPage.cmsoid}
                  pageTitle={historyPage.title}
                />
              )}
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => navigate(`/admin/content/pages/${historyPage.cmsoid}`)}
              >
                <Edit3 className="h-4 w-4" />
                Open editor
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New page from template</DialogTitle>
          </DialogHeader>
          {templates.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No templates yet. Create one under Content → Templates.
            </p>
          ) : (
            <ul className="max-h-80 divide-y overflow-y-auto">
              {templates.map((t) => (
                <li key={t.ctplid} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.name}</p>
                    <p className="text-xs text-gray-500">
                      {t.ctype ?? 'any type'}
                      {t.title_tpl ? ` · ${t.title_tpl}` : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    disabled={applyMutation.isPending}
                    onClick={() => handleApplyTemplate(t.ctplid, t.title_tpl || t.name)}
                  >
                    Use
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSPagesListPage;
